import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  AlarmClock,
  Check,
  Clock,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import CreateTask from "./Create";
import { deleteTask, getTasks } from "../../../../services/tasks";
import { tasksInformation } from "../../../../constants";
import { Button } from "../../../../components/ui/button";
import { Card } from "../../../../components/ui/card";
import { Avatar } from "../../../../components/ui/avatar";
import { Badge } from "../../../../components/ui/badge";
import { ConfirmDialog } from "../../../../components/ui/dialog";
import { EmptyState } from "../../../../components/ui/empty-state";
import { Pagination } from "../../../../components/ui/pagination";
import { Tooltip } from "../../../../components/ui/tooltip";
import { Spinner } from "../../../../components/ui/spinner";
import { toast } from 'sonner'

export default function Tasks({ assistantId }) {
  return (
    <Routes>
      <Route path={"/"} element={<TaskList assistantId={assistantId} />} />
      <Route path={"/create"} element={<CreateTask assistantId={assistantId} />} />
    </Routes>
  );
}

const PAGE_SIZE = 5;

function TaskList({ assistantId }) {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const getTaskList = useCallback(async () => {
    setLoading(true);
    try {
      const tasksRes = await getTasks(assistantId);
      setTasks(tasksRes.data || []);
    } catch (err) {
      toast.error("Não foi possível carregar tarefas");
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => {
    getTaskList();
  }, [getTaskList]);

  const paginatedTasks = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return tasks.slice(start, start + PAGE_SIZE);
  }, [tasks, page]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <AlarmClock size={18} style={{ color: "var(--dash-primary)" }} />
            <h2
              className="text-xl font-semibold tracking-tight"
              style={{ color: "var(--dash-text)" }}
            >
              Tarefas agendadas
            </h2>
          </div>
          <p
            className="text-xs"
            style={{ color: "var(--dash-text-muted)" }}
          >
            Configure ações automáticas que ocorrem em horários previamente agendados.
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={Plus}
          onClick={() => navigate("create")}
        >
          Criar tarefa
        </Button>
      </header>

      {loading ? (
        <Card className="flex items-center justify-center py-16">
          <Spinner size={20} />
        </Card>
      ) : tasks.length === 0 ? (
        <Card>
          <EmptyState
            icon={AlarmClock}
            title="Nenhuma tarefa agendada"
            description="Tarefas são eventos que ocorrem em horários previamente agendados. Use-as para enviar mensagens ativas ou disparar ações."
            action={
              <Button
                variant="primary"
                size="lg"
                leftIcon={Plus}
                onClick={() => navigate("create")}
              >
                Criar primeira tarefa
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {paginatedTasks.map((task) => (
            <TaskItem key={task.id} item={task} onSuccess={getTaskList} />
          ))}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={tasks.length}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  WAITING: { variant: "warning", label: "Aguardando", icon: RefreshCw },
  DONE: { variant: "success", label: "Concluída", icon: Check },
  ERROR: { variant: "danger", label: "Erro", icon: X },
};

function TaskItem({ item, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const taskInfo = useMemo(() => {
    return (
      tasksInformation.find((t) => t.type === item.type) || {
        title: item.type,
        description: "",
      }
    );
  }, [item.type]);

  const status = STATUS_CONFIG[item.status] || STATUS_CONFIG.WAITING;
  const StatusIcon = status.icon;

  const description = useMemo(() => {
    if (item?.triggerType === "CRONJOB") {
      return "Agendamento contínuo · " + item.triggerData;
    }
    if (item?.triggerData) {
      const parts = item.triggerData.split(" ");
      const time = parts.length >= 2 ? parts[1] + ":" + parts[0] : item.triggerData;
      if (item?.status === "WAITING") return "Aguardando horário · " + time;
      if (item?.status === "DONE") return "Executada às " + time;
    }
    return item?.triggerType + " · " + (item?.triggerData || "");
  }, [item]);

  function onDelete() {
    setDeleting(true);
    deleteTask(item.assistantID, item.id)
      .then(() => {
        toast.success("Tarefa deletada");
        setConfirmOpen(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error("Não foi possível deletar a tarefa");
        console.log(err);
      })
      .finally(() => setDeleting(false));
  }

  return (
    <>
      <Card className="flex items-center gap-4 p-4 transition-all hover:border-[var(--dash-primary-border)]">
        <Avatar
          variant={status.variant}
          icon={StatusIcon}
          size="lg"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3
              className="truncate text-sm font-semibold"
              style={{ color: "var(--dash-text)" }}
            >
              {taskInfo.title}
            </h3>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <p
            className="truncate text-xs"
            style={{ color: "var(--dash-text-muted)" }}
          >
            <Clock size={10} className="-mt-0.5 mr-1 inline" />
            {description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip content="Em breve">
            <Button variant="ghost" size="sm" disabled>
              Editar
            </Button>
          </Tooltip>
          <Tooltip content="Deletar tarefa">
            <Button
              variant="danger"
              size="icon-sm"
              onClick={() => setConfirmOpen(true)}
              disabled={deleting}
            >
              <Trash2 size={14} />
            </Button>
          </Tooltip>
        </div>
      </Card>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onDelete}
        title="Deletar tarefa?"
        description="Esta ação irá remover permanentemente a tarefa agendada. Não é possível desfazer."
        confirmLabel="Deletar"
        loading={deleting}
      />
    </>
  );
}
